import { type FlagName, useFlagSnapshot } from '@platform/feature-flags';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { flaggedLegacyRedirectMap } from '../../config/redirects';
import { env } from '../../config/runtime';
import { resolveLegacyRedirect } from './redirect-service';

export type RedirectTarget = { kind: 'external'; url: string } | { kind: 'internal'; path: string };

export function resolveLegacyRedirectTarget(
  legacyPath: string,
  domain: string,
  isFlagOn: (flag: FlagName) => boolean,
): RedirectTarget | undefined {
  const flagged = flaggedLegacyRedirectMap.get(legacyPath);
  if (flagged) {
    return isFlagOn(flagged.flag) ? { kind: 'internal', path: flagged.whenOn } : { kind: 'external', url: flagged.whenOff(domain) };
  }
  const staticUrl = resolveLegacyRedirect(legacyPath, domain);
  return staticUrl ? { kind: 'external', url: staticUrl } : undefined;
}

export function useLegacyRedirect(legacyPath: string | undefined): boolean {
  const flags = useFlagSnapshot();
  const navigate = useNavigate();

  const target = legacyPath ? resolveLegacyRedirectTarget(legacyPath, env.waipuDomain, (flag) => flags[flag] === true) : undefined;
  const internalPath = target?.kind === 'internal' ? target.path : undefined;
  const externalUrl = target?.kind === 'external' ? target.url : undefined;

  useEffect(() => {
    if (internalPath) {
      void navigate({ to: internalPath, replace: true });
    } else if (externalUrl) {
      window.location.replace(externalUrl);
    }
  }, [internalPath, externalUrl, navigate]);

  return internalPath !== undefined || externalUrl !== undefined;
}
